import React from 'react';
import PageHero from '../components/PageHero';
import Status from '../components/Status';
import { Link } from 'react-router-dom';
import { NOTES } from '../content/notes';
import { STORY_SLUGS } from '../content/stories/slugs';

const Notes: React.FC = () => {
  const published = NOTES.filter((n) => n.status === 'published');
  const planned = NOTES.filter((n) => n.status === 'planned');

  return (
    <div>
      <PageHero
        title="Technical Notes"
        lead="Short, reproducible analyses of U.S. load-forecasting and data-quality problems. Each note has a date, the code behind it, and a DOI. This page is the canonical version; notes are republished on Medium, LinkedIn and Energy Central."
      />

      <section className="bg-white section-padding">
        <div className="container-width">
          {published.length > 0 && (
            <>
              <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-8">Published</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
                {published.map((n) => {
                  // The whole card opens the reading page: the title's link is stretched over the card, and the other
                  // links sit above that layer so they keep their own targets.
                  const readable = STORY_SLUGS.includes(n.slug);
                  return (
                    <article
                      key={n.slug}
                      className={`relative bg-gray-50 rounded-xl p-8 shadow transition-shadow flex flex-col ${
                        readable ? 'hover:shadow-lg hover:bg-gray-100 cursor-pointer' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <Status tone="live">Published</Status>
                        <span className="text-sm text-gray-500">{n.date}</span>
                      </div>
                      <h3 className="text-xl font-poppins font-semibold text-dark-gray mb-3">
                        {readable ? (
                          <Link
                            to={`/notes/${n.slug}`}
                            className="after:absolute after:inset-0 after:rounded-xl focus:outline-none focus-visible:underline"
                          >
                            {n.title}
                          </Link>
                        ) : (
                          n.title
                        )}
                      </h3>
                      <p className="text-gray-600 mb-4 flex-1">{n.summary}</p>
                      <div className="relative z-10 flex flex-wrap gap-4 text-sm">
                        {n.doi && (
                          <a href={`https://doi.org/${n.doi}`} target="_blank" rel="noopener noreferrer" className="text-light-blue hover:underline">
                            DOI {n.doi} (PDF)
                          </a>
                        )}
                        {n.code && (
                          <a href={n.code} target="_blank" rel="noopener noreferrer" className="text-dark-gray hover:underline">
                            Full note and code
                          </a>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}

          <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-2">Planned</h2>
          <p className="text-gray-600 mb-8">
            The publication schedule. Titles may change as the analysis is done; dates are the target month.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {planned.map((n) => (
              <article key={n.slug} className="bg-gray-50 rounded-xl p-8 shadow flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  <Status tone="planned">Planned</Status>
                  <span className="text-sm text-gray-500">{n.month}</span>
                </div>
                <h3 className="text-xl font-poppins font-semibold text-dark-gray mb-3">{n.title}</h3>
                <p className="text-gray-600 flex-1">{n.summary}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 section-padding">
        <div className="container-width">
          <h2 className="text-2xl font-poppins font-bold text-dark-gray mb-4">How notes are published</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>The note and its notebook are published here and in the GitHub repository.</li>
            <li>The same day, the PDF is archived on Zenodo and receives a DOI.</li>
            <li>Within 48 hours it is republished on Medium, LinkedIn and Energy Central, pointing back to this page.</li>
            <li>Corrections are made here first and noted in the changelog of the archived record.</li>
          </ol>
        </div>
      </section>
    </div>
  );
};

export default Notes;
